import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  HUBUP_NOTIFICATION_AI_LANGUAGES,
  findNotificationAiLanguage,
  notificationAiFlagUrl,
} from '../../lib/hubupNotificationAiLanguages';

function FlagImg({ flagCountry, className }) {
  const src = notificationAiFlagUrl(flagCountry);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      width={20}
      height={15}
      className={cn(
        'h-[15px] w-5 shrink-0 rounded-sm border border-border/60 object-cover',
        className,
      )}
      loading="lazy"
      decoding="async"
    />
  );
}

export default function NotificationAiLanguageSelect({
  id,
  value,
  onChange,
  className,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = findNotificationAiLanguage(value);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        id={id}
        type="button"
        className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex min-w-0 items-center gap-2 truncate">
          {selected ? (
            <>
              <FlagImg flagCountry={selected.flagCountry} />
              <span className="truncate">{selected.label}</span>
            </>
          ) : (
            <span className="text-muted-foreground">Choisir une langue</span>
          )}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <ul
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-card py-1 shadow-elevation-2"
          role="listbox"
          aria-labelledby={id}
        >
          {HUBUP_NOTIFICATION_AI_LANGUAGES.map((opt) => {
            const isSelected = opt.code === value;
            return (
              <li key={opt.code} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted',
                    isSelected && 'bg-muted/80',
                  )}
                  onClick={() => {
                    onChange?.(opt.code);
                    setOpen(false);
                  }}
                >
                  <FlagImg flagCountry={opt.flagCountry} />
                  <span className="flex-1 truncate">{opt.label}</span>
                  {isSelected ? (
                    <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
